/**
 * Records Center — Enterprise Records Management System
 * 
 * A Record is an intelligent business object, not just a file.
 * Records actively participate in platform intelligence, compliance,
 * workflow, scheduling, and operational decision making.
 * 
 * Philosophy:
 * - "Enter Information Once. Use It Everywhere."
 * - "Records are platform objects, not files."
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileText, Upload, Search, Filter, Clock, AlertTriangle,
  CheckCircle, XCircle, Eye, Download, MoreVertical,
  RefreshCw, Folder, Tag, Calendar, User, Shield, Trash2,
  FileCheck, FileClock, FileX, FilePlus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { api } from '../../lib/api';

// Status configuration
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: FileText },
  pending_review: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800', icon: FileClock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: FileCheck },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: FileX },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  superseded: { label: 'Superseded', color: 'bg-gray-100 text-gray-800', icon: FileText },
  archived: { label: 'Archived', color: 'bg-gray-100 text-gray-800', icon: Folder },
};

// Category icons
const CATEGORY_ICONS = {
  identification: Shield,
  medical: FileText,
  legal: FileText,
  training: FileText,
  compliance: Shield,
  employment: User,
  client: User,
  financial: FileText,
  operational: FileText,
  communication: FileText,
  incident: AlertTriangle,
  template: FileText,
  other: FileText,
};

export default function RecordsCenter() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    status: searchParams.get('status') || '',
    search: searchParams.get('q') || '',
  });
  
  const [uploadDialog, setUploadDialog] = useState(false);
  const [reviewDialog, setReviewDialog] = useState({ open: false, record: null });
  const [reviewNotes, setReviewNotes] = useState('');

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, [filters.category, filters.status]);

  const fetchData = async () => {
    try {
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.status) params.status = filters.status;
      if (filters.search) params.q = filters.search;

      const [recordsRes, statsRes] = await Promise.all([
        api.get('/records/search', { params }),
        api.get('/records/stats')
      ]);
      setRecords(recordsRes.data.items || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch records:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get('/records/categories');
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const handleReviewAction = async (action) => {
    if (!reviewDialog.record) return;
    
    try {
      await api.post(`/records/${reviewDialog.record.id}/review`, {
        action,
        notes: reviewNotes
      });
      setReviewDialog({ open: false, record: null });
      setReviewNotes('');
      await fetchData();
    } catch (error) {
      console.error('Review failed:', error);
    }
  };

  const handleDownload = async (record) => {
    try {
      const response = await api.get(`/records/${record.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', record.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="records-center">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Records Center</h1>
          <p className="text-gray-500 mt-1">Enterprise records management. Records are platform objects.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setUploadDialog(true)}
            data-testid="upload-record-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Record
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard
            label="Total Records"
            value={stats.total}
            color="blue"
            icon={FileText}
          />
          <StatCard
            label="Pending Review"
            value={stats.pending_review}
            color="yellow"
            icon={FileClock}
            onClick={() => setFilters({ ...filters, status: 'pending_review' })}
          />
          <StatCard
            label="Expired"
            value={stats.expired}
            color="red"
            icon={FileX}
            onClick={() => setFilters({ ...filters, status: 'expired' })}
          />
          <StatCard
            label="Expiring Soon"
            value={stats.expiring_soon}
            color="orange"
            icon={AlertTriangle}
          />
          <StatCard
            label="Awaiting Signature"
            value={stats.awaiting_signature}
            color="purple"
            icon={FilePlus}
          />
        </div>
      )}

      {/* Category Pills */}
      {stats?.by_category && Object.keys(stats.by_category).length > 0 && (
        <div className="flex flex-wrap gap-2 bg-white p-4 rounded-lg border">
          <Button
            size="sm"
            variant={!filters.category ? 'default' : 'outline'}
            onClick={() => setFilters({ ...filters, category: '' })}
          >
            All Categories
          </Button>
          {Object.entries(stats.by_category).map(([cat, count]) => (
            <Button
              key={cat}
              size="sm"
              variant={filters.category === cat ? 'default' : 'outline'}
              onClick={() => setFilters({ ...filters, category: cat })}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)} ({count})
            </Button>
          ))}
        </div>
      )}

      {/* Filters */}
      <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4 bg-white p-4 rounded-lg border">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search records..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
        </div>
        <Select
          value={filters.status || "all"}
          onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? "" : value })}
        >
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending_review">Pending Review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" variant="outline">
          <Search className="h-4 w-4" />
        </Button>
      </form>

      {/* Records List */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {records.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No records found</h3>
            <p className="text-gray-500 mt-1">Upload your first record to get started.</p>
            <Button className="mt-4" onClick={() => setUploadDialog(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Upload Record
            </Button>
          </div>
        ) : (
          <div className="divide-y">
            {records.map((record) => (
              <RecordRow
                key={record.id}
                record={record}
                onView={() => navigate(`/records/${record.id}`)}
                onDownload={() => handleDownload(record)}
                onReview={() => setReviewDialog({ open: true, record })}
                formatDate={formatDate}
                formatFileSize={formatFileSize}
              />
            ))}
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      <UploadDialog
        open={uploadDialog}
        onClose={() => setUploadDialog(false)}
        onSuccess={() => {
          setUploadDialog(false);
          fetchData();
        }}
        categories={categories}
      />

      {/* Review Dialog */}
      <Dialog open={reviewDialog.open} onOpenChange={(open) => setReviewDialog({ open, record: reviewDialog.record })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Record</DialogTitle>
            <DialogDescription>
              {reviewDialog.record?.file_name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Review notes (optional)..."
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              rows={3}
              data-testid="review-notes"
            />
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setReviewDialog({ open: false, record: null })}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleReviewAction('reject')}
              data-testid="reject-btn"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reject
            </Button>
            <Button onClick={() => handleReviewAction('approve')} data-testid="approve-btn">
              <CheckCircle className="h-4 w-4 mr-2" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, color, icon: Icon, onClick }) {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    orange: 'bg-orange-50 border-orange-200 text-orange-900',
    purple: 'bg-purple-50 border-purple-200 text-purple-900',
    green: 'bg-green-50 border-green-200 text-green-900',
  };

  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`p-4 rounded-lg border ${colorClasses[color]} text-left hover:shadow-sm transition-shadow ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 opacity-70" />
        <span className="text-sm opacity-80">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </button>
  );
}

// Record Row Component
function RecordRow({ record, onView, onDownload, onReview, formatDate, formatFileSize }) {
  const status = STATUS_CONFIG[record.status] || STATUS_CONFIG.draft;
  const StatusIcon = status.icon;
  const CategoryIcon = CATEGORY_ICONS[record.category] || FileText;

  return (
    <div
      className={`p-4 hover:bg-gray-50 transition-colors ${record.is_expired ? 'border-l-4 border-red-500' : ''}`}
      data-testid={`record-${record.id}`}
    >
      <div className="flex items-start gap-4">
        {/* File Icon */}
        <div className="p-2 rounded-lg bg-gray-100">
          <CategoryIcon className="h-5 w-5 text-gray-600" />
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-gray-900 truncate">{record.file_name}</span>
            {record.is_expired && (
              <Badge className="bg-red-100 text-red-800">EXPIRED</Badge>
            )}
            {record.days_until_expiry && record.days_until_expiry <= 30 && record.days_until_expiry > 0 && (
              <Badge className="bg-orange-100 text-orange-800">
                Expires in {record.days_until_expiry}d
              </Badge>
            )}
          </div>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Folder className="h-3 w-3" />
              {record.category}
            </span>
            <span className="flex items-center gap-1">
              {record.record_type.replace(/_/g, ' ')}
            </span>
            {record.owner_entity_name && (
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {record.owner_entity_name}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formatDate(record.uploaded_at)}
            </span>
            <span>{formatFileSize(record.file_size)}</span>
            <Badge className={status.color}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {status.label}
            </Badge>
          </div>

          {/* Tags */}
          {record.tags && record.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {record.tags.map((tag, i) => (
                <Badge key={i} variant="outline" className="text-xs">
                  <Tag className="h-2 w-2 mr-1" />
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={onView} data-testid={`view-${record.id}`}>
            <Eye className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onDownload} data-testid={`download-${record.id}`}>
            <Download className="h-4 w-4" />
          </Button>

          {record.review_status === 'pending' && (
            <Button
              size="sm"
              variant="outline"
              className="text-green-600 hover:text-green-700"
              onClick={onReview}
              data-testid={`review-${record.id}`}
            >
              <CheckCircle className="h-4 w-4" />
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onView}>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </DropdownMenuItem>
              {record.review_status === 'pending' && (
                <DropdownMenuItem onClick={onReview}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Review
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}

// Upload Dialog Component
function UploadDialog({ open, onClose, onSuccess, categories }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    owner_entity_type: 'client',
    owner_entity_id: '',
    owner_entity_name: '',
    category: 'other',
    record_type: 'other',
    tags: '',
    expiration_date: '',
    requires_review: true,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;

    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', file);
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== '') {
          data.append(key, value.toString());
        }
      });

      await api.post('/records/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      onSuccess();
      setFile(null);
      setFormData({
        owner_entity_type: 'client',
        owner_entity_id: '',
        owner_entity_name: '',
        category: 'other',
        record_type: 'other',
        tags: '',
        expiration_date: '',
        requires_review: true,
      });
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Record</DialogTitle>
          <DialogDescription>
            Upload a new record to the Records Center
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* File Input */}
          <div className="border-2 border-dashed rounded-lg p-6 text-center">
            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
              id="file-upload"
              data-testid="file-input"
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
              {file ? (
                <p className="text-sm text-gray-900">{file.name}</p>
              ) : (
                <p className="text-sm text-gray-500">Click to select a file</p>
              )}
            </label>
          </div>

          {/* Owner */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Entity Type</label>
              <Select
                value={formData.owner_entity_type}
                onValueChange={(v) => setFormData({ ...formData, owner_entity_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="agency">Agency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Entity ID</label>
              <Input
                value={formData.owner_entity_id}
                onChange={(e) => setFormData({ ...formData, owner_entity_id: e.target.value })}
                placeholder="Entity ID"
              />
            </div>
          </div>

          {/* Category & Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Category</label>
              <Select
                value={formData.category}
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.key} value={cat.key}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Expiration Date</label>
              <Input
                type="date"
                value={formData.expiration_date}
                onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="text-sm font-medium">Tags (comma-separated)</label>
            <Input
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="consent, signed, 2026"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!file || uploading}>
              {uploading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
